import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import AuthCardFooter from "../../components/auth/AuthCardFooter";
import styles from "./AuthPage.module.css";

/**
 * Screen 3b — shown when /users/password-reset/ rejects the code as expired or
 * already used. The backend has no separate verify step, so this is reached
 * from the set-password screen after classifyResetError() tags the failure.
 * Reads { variant: "expired" | "used", email } from router state.
 */
const COPY = {
  // Default: the backend returns a single `invalid_or_expired_code` for any bad
  // code (it cannot tell a typo from a truly expired one), so the honest copy
  // covers both. `expired` / `used` are reserved for distinct signals if the
  // backend ever sends them.
  invalid: {
    title: "Invalid or expired code",
    message:
      "This verification code is invalid or has expired. Request a new code to reset your password.",
  },
  expired: {
    title: "This code has expired",
    message:
      "Your verification code is no longer valid. Request a new code to reset your password.",
  },
  used: {
    title: "This code has already been used",
    message:
      "This verification code has already been used. Request a new code to reset your password.",
  },
};

const ResetCodeError = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const variant = COPY[location.state?.variant] ? location.state.variant : "invalid";
  const email = location.state?.email;

  // Guard: this screen is only meaningful when reached from the reset flow.
  useEffect(() => {
    if (!location.state?.variant) {
      navigate("/recover-password", { replace: true });
    }
  }, [location.state, navigate]);

  const copy = COPY[variant];

  return (
    <>
      <div className={styles.warningBadge} aria-hidden="true">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="9" />
          <line x1="12" y1="7" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12" y2="17" />
        </svg>
      </div>

      <h1 className={styles.errorTitle}>{copy.title}</h1>
      <p className={`${styles.subheading} ${styles.centerText}`}>
        {copy.message}
      </p>

      <button
        type="button"
        className={styles.primaryBtn}
        onClick={() =>
          navigate("/recover-password", {
            replace: true,
            state: email ? { email } : undefined,
          })
        }
      >
        Request New Code
      </button>

      <Link to="/login" className={styles.secondaryBtn}>
        Back to login
      </Link>

      <AuthCardFooter />
    </>
  );
};

export default ResetCodeError;
